import { Icon } from '@iconify/react'
import { Button, Divider, Select, Tag } from 'antd'
import { CustomTagProps } from 'rc-select/lib/BaseSelect'
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'

import { useAllLabelsQuery } from '../../../graphql/generated'
import { useAppSelector } from '../../../redux/hooks'
import {
  ColorPalleteDeafultOption,
  ColorPalleteKey,
  ColorPalleteOptions,
} from '../../utils/collorPallete'
import { IconPalleteKey, IconPalleteOptions } from '../../utils/iconPallete'
import { useRecentLabelOrder } from '../../utils/useRecentLabelOrder'
import { AddNewLabelModalButton } from '../AddNewLabelModalButton/AddNewLabelModalButton'
import { Label } from '../Label/Label'

export interface LabelSelectInputProps {
  onChange?: (value: string[] | []) => void
  initialValue?: string[] | []
  value?: string[] | [] // controlled variant; if defined, ignores initialValue
  disabled?: boolean
  disableNew?: boolean
}

const dropdownStyle = { overflow: 'visible' }

export const LabelSelectInput: FunctionComponent<LabelSelectInputProps> = ({
  initialValue,
  value,
  disableNew,
  onChange,
  ...props
}) => {
  const tenantId = useAppSelector(state => state.myProfile.tenantId)
  const [allLabels] = useAllLabelsQuery({
    variables: { accountId: tenantId },
  })
  const [open, setOpen] = useState<undefined | boolean>(undefined)

  const labels = (allLabels.data?.allLabels?.nodes || []).filter(
    (label): label is NonNullable<typeof label> => !!label
  )
  const { orderedLabels, applySelection } = useRecentLabelOrder(labels, {
    disableReorder: disableNew,
    initialSelectedIds:
      (value as string[] | undefined) ||
      (initialValue as string[] | undefined) ||
      [],
  })

  const handleClose = useCallback(() => {
    // hackaround: force close Select and then set to default, to preserve native Select behavior
    setOpen(false)
    setTimeout(() => {
      setOpen(undefined)
    }, 0)
    if (open === false) setOpen(undefined)
  }, [open])

  const handleSelectChange = useCallback(
    (nextSelectedIds: string[]) => {
      applySelection(nextSelectedIds)
      onChange?.(nextSelectedIds)
    },
    [applySelection, onChange]
  )

  const tagRender = useCallback(
    (customProps: CustomTagProps) => {
      const findLabel = orderedLabels.find(
        label => label.id === customProps.value
      )
      return (
        <Tag
          style={{ padding: 0, paddingRight: 7, borderLeft: 0 }}
          {...customProps}
        >
          <Label
            colorKey={(findLabel?.colorKey as ColorPalleteKey) || undefined}
            iconKey={(findLabel?.iconKey as IconPalleteKey) || undefined}
            description={findLabel?.description || undefined}
            name={findLabel?.name || undefined}
          />
        </Tag>
      )
    },
    [orderedLabels]
  )

  const dropdownRender = useCallback(
    (menu: React.ReactElement) => (
      <div id="selectLabelDropDownRender">
        {menu}
        <Divider style={{ margin: '8px 0' }} />
        <div className="flex space-x-2">
          {!disableNew ? (
            <AddNewLabelModalButton onClick={handleClose} isSelectBox />
          ) : (
            <div className="w-full" />
          )}
          <Button
            onClick={handleClose}
            type="primary"
            ghost
            className="border border-l"
          >
            OK
          </Button>
        </div>
      </div>
    ),
    [handleClose, disableNew]
  )
  const options = useMemo(
    () =>
      orderedLabels.map(label => ({
        name: label.name,
        value: label.id,
        label: (
          <div className="flex items-center">
            <div
              className={
                'w-5 h-5 border rounded-sm mr-2 flex items-center justify-center ' +
                (!!label.colorKey &&
                  label.colorKey !== ColorPalleteDeafultOption &&
                  ColorPalleteOptions[label.colorKey as ColorPalleteKey])
              }
            >
              {!!label.iconKey && (
                <Icon
                  width={10}
                  icon={IconPalleteOptions[label.iconKey as IconPalleteKey]}
                />
              )}
            </div>
            <div className="truncate w-full">
              {label.name}
              {label.description && (
                <span className="text-gray-400 pl-2">{label.description}</span>
              )}
            </div>
          </div>
        ),
      })),
    [orderedLabels]
  )

  return (
    <Select
      // controlled if value !== undefined
      value={value !== undefined ? (value as string[]) : undefined}
      // uncontrolled fallback with defaultValue
      {...(value === undefined ? { defaultValue: initialValue } : {})}
      className="w-full"
      showSearch
      placeholder="Vyberte štítek"
      optionFilterProp="name"
      mode="multiple"
      dropdownStyle={dropdownStyle}
      tagRender={tagRender}
      dropdownRender={dropdownRender}
      open={open}
      options={options}
      popupClassName="!w-[480px] !min-w-0"
      popupMatchSelectWidth={false}
      onChange={handleSelectChange}
      {...props}
    />
  )
}
